"use client";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
} from "@/components/ui/pagination";
import { Link } from "@/i18n/navigation";
import { cn } from "@/lib/utils";
import { BlogMeta } from "@/types/blogs/Blogs";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useSearchParams } from "next/navigation";

const navButtonClass =
  "flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background text-foreground transition-colors hover:border-primary hover:text-primary";

const pageButtonClass = (isActive?: boolean) =>
  cn(
    "flex h-10 w-10 items-center justify-center rounded-[10px] border text-sm font-500 transition-colors",
    isActive
      ? "border-primary bg-primary text-primary-foreground"
      : "border-border bg-background text-foreground hover:border-primary hover:text-primary",
  );

export function BlogPagination({
  blogMeta,
  slug,
}: {
  blogMeta: BlogMeta;
  slug?: string;
}) {
  const { currentPage, lastPage } = blogMeta;
  const searchParams = useSearchParams();

  const getPageUrl = (page: number) => {
    const params = new URLSearchParams(searchParams.toString());
    if (page <= 1) {
      params.delete("page");
    } else {
      params.set("page", page.toString());
    }
    const queryString = params.toString();
    const basePath = slug ? `/blog/${slug}` : "/blog";
    return queryString ? `${basePath}?${queryString}` : basePath;
  };

  return (
    <Pagination>
      <PaginationContent className="gap-2 sm:gap-2">
        <PaginationItem>
          {currentPage === 1 ? (
            <span
              aria-hidden
              className={cn(navButtonClass, "pointer-events-none opacity-40")}
            >
              <ChevronLeft size={18} />
            </span>
          ) : (
            <Link
              href={getPageUrl(currentPage - 1)}
              aria-label="Go to previous page"
              className={navButtonClass}
            >
              <ChevronLeft size={18} />
            </Link>
          )}
        </PaginationItem>

        {currentPage == 2 && (
          <PaginationItem>
            <Link href={getPageUrl(1)} className={pageButtonClass()}>
              {currentPage - 1}
            </Link>
          </PaginationItem>
        )}
        {currentPage > 2 && (
          <PaginationItem>
            <Link
              href={getPageUrl(currentPage - 1)}
              className={pageButtonClass()}
            >
              {currentPage - 1}
            </Link>
          </PaginationItem>
        )}

        <PaginationItem>
          <span aria-current="page" className={pageButtonClass(true)}>
            {currentPage}
          </span>
        </PaginationItem>

        {currentPage + 1 < lastPage && (
          <PaginationItem>
            <Link
              href={getPageUrl(currentPage + 1)}
              className={pageButtonClass()}
            >
              {currentPage + 1}
            </Link>
          </PaginationItem>
        )}

        {currentPage + 2 < lastPage && (
          <PaginationItem className="hidden md:block">
            <Link
              href={getPageUrl(currentPage + 2)}
              className={pageButtonClass()}
            >
              {currentPage + 2}
            </Link>
          </PaginationItem>
        )}
        {lastPage > 4 && currentPage !== lastPage && (
          <PaginationItem>
            <PaginationEllipsis className="h-10 w-10 text-muted-foreground" />
          </PaginationItem>
        )}

        {currentPage !== lastPage && (
          <PaginationItem>
            <Link href={getPageUrl(lastPage)} className={pageButtonClass()}>
              {lastPage}
            </Link>
          </PaginationItem>
        )}

        <PaginationItem>
          {currentPage === lastPage ? (
            <span
              aria-hidden
              className={cn(navButtonClass, "pointer-events-none opacity-40")}
            >
              <ChevronRight size={18} />
            </span>
          ) : (
            <Link
              href={getPageUrl(currentPage + 1)}
              aria-label="Go to next page"
              className={navButtonClass}
            >
              <ChevronRight size={18} />
            </Link>
          )}
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}
